import { OfficerData } from '../data/mockOfficers'

export interface ProcessedOfficer extends OfficerData {
  borrowerMin: number // onCallMin
  availableMin: number // readyMin — positive effort
  wrapUpMin: number // afterCallWorkMin — cautionary
  offQueueMin: number // notReadyMin — negative
  ringingMin: number // de-emphasized
  positiveEffortMin: number // borrower + available
  focusPct: number // positiveEffort / login — EFFORT signal
  outputScore: number // 0–100 from leads + submissions
  productionScore: number // 0–100, the unified measure
  wrapUpRatio: number
  offQueueRatio: number
  tier: TierKey // now derived from productionScore
  hasPartialData: boolean
  isWarmPhone: boolean // high focus % but zero output
}

export type TierKey = 'pacesetter' | 'climbing' | 'steady' | 'coaching'

export const TIER_LABELS: Record<TierKey, string> = {
  pacesetter: 'Pacesetter',
  climbing: 'Climbing',
  steady: 'Steady',
  coaching: 'Needs Coaching',
}

export const TIER_RING_CLASS: Record<TierKey, string> = {
  pacesetter: 'ring-signal',
  climbing: 'ring-ink',
  steady: 'ring-bone-3',
  coaching: 'ring-ember',
}

// Output points: submissions weighted 2× leads since they're harder.
// A strong day (e.g. 5 leads + 3 subs = 88) approaches the 100 cap.
export function computeOutputScore(leads: number, subs: number): number {
  return Math.min(100, leads * 8 + subs * 16)
}

// Unified measure. Output is the goal; effort still matters so a lucky day
// with a few easy submissions doesn't crown someone who barely worked.
export function computeProductionScore(
  effortPct: number,
  outputScore: number,
): number {
  return effortPct * 0.4 + outputScore * 0.6
}

// Tiers now derive from Production Score.
export function getTier(productionScore: number): TierKey {
  if (productionScore >= 75) return 'pacesetter'
  if (productionScore >= 60) return 'climbing'
  if (productionScore >= 45) return 'steady'
  return 'coaching'
}

export function processOfficer(o: OfficerData): ProcessedOfficer {
  const borrowerMin = o.onCallMin
  const availableMin = o.readyMin
  const wrapUpMin = o.afterCallWorkMin
  const offQueueMin = o.notReadyMin
  const ringingMin = o.ringingMin
  const positiveEffortMin = borrowerMin + availableMin
  const focusPct = o.loginMin > 0 ? (positiveEffortMin / o.loginMin) * 100 : 0
  const wrapUpRatio = o.loginMin > 0 ? wrapUpMin / o.loginMin : 0
  const offQueueRatio = o.loginMin > 0 ? offQueueMin / o.loginMin : 0
  const accounted =
    borrowerMin + availableMin + wrapUpMin + offQueueMin + ringingMin
  const hasPartialData = o.loginMin > 0 && accounted < o.loginMin * 0.9

  const outputScore = computeOutputScore(o.leadsConverted, o.submissions)
  const productionScore = computeProductionScore(focusPct, outputScore)

  // Warm phone: looks busy on the phones but produced nothing.
  const isWarmPhone =
    focusPct >= 75 &&
    o.leadsConverted === 0 &&
    o.submissions === 0 &&
    !hasPartialData

  return {
    ...o,
    borrowerMin,
    availableMin,
    wrapUpMin,
    offQueueMin,
    ringingMin,
    positiveEffortMin,
    focusPct,
    outputScore,
    productionScore,
    wrapUpRatio,
    offQueueRatio,
    tier: getTier(productionScore),
    hasPartialData,
    isWarmPhone,
  }
}

export function formatHm(minutes: number): string {
  if (!minutes || minutes <= 0) return '0m'
  const h = Math.floor(minutes / 60)
  const m = Math.round(minutes % 60)
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}

export function formatLongDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  const dt = new Date(y, m - 1, d)
  return dt.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}
